import React, { useContext, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { Icon, Input, MessageBox, Tab, Tabs, Typography } from '@dw/helix'
import { API } from '../../utils'

import { ArticlesContext } from '../../contexts'
import PONumberInformation from './PONumberInformation'
import PONumberSerialsInformation from './PONumberSerialsInformation'
import ToBeGeneratedForm from './ToBeGeneratedForm'
import DownloadPOForm from './DownloadPOForm'
import MissingValuesForm, { MissingValuesPayload } from './MissingValuesForm'
import ConfirmSerialNumberGeneration from './ConfirmSerialNumberGeneration'

import isObjectEmpty from '../../utils/isObjectEmpty'
import downloadFile from '../../utils/downloadFile'

interface GenerateSerialNumbersPageProps {
  history: object
  location: object
  match: object
}

export interface PurchaseOrderArticle {
  articleId: string
  lineNo: number
  quantity: number
  updatedAt: number
}

export interface PurchaseOrder {
  [indexSignature: string]: string | null | number | PurchaseOrderArticle[]
  vendorName: string
  assignedUserId: null | string
  purchaserCode: string
  orderId: string
  updatedAt: number
  createdAt: number
  vendorId: string
  items: PurchaseOrderArticle[]
  regionId: string
  serialnumberStatus: 'PROCESSED' | 'NEW'
}

const pageStyles = css`
  margin: -5px -64px 0;
  @media (max-width: 1199px) {
    margin-left: 0;
    margin-right: 0;
  }
`
const panelsWrapperStyles = css`
  display: grid;
  grid-template-columns: 633px 1fr;
  grid-gap: 30px;
  margin-top: 20px;
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`
const purchaseOrdersPanelStyles = css`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  padding: 30px 20px;
  margin-bottom: 40px;
  border-radius: 2px;
  box-shadow: 0 1px 7px 0 rgba(0, 0, 0, 0.19), 0 3px 3px 0 rgba(0, 0, 0, 0.13);

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0);
    transition: background-color 0.3s ease;
    top: 0;
    left: 0;
    pointer-events: none;
  }
`
const disabledPanel = css`
  pointer-events: none;
  &::after {
    background-color: rgba(255, 255, 255, 0.8);
    pointer-events: all;
  }
`

const icoLeftStyles = css`
  margin-bottom: 1px;
`

interface MessageProps {
  type?: 'error' | 'success' | 'primary'
  text?: string
}

interface PanelTypeConfig {
  type?: string
  data?: any
}

const APPROVED_VENDOR_IDS = [
  'V1231',
  'V1849',
  'V100014',
  'V2071',
  'V1000000385',
  'V1000001653',
  'V1000001047',
  'V1000000564',
  'V1000002527',
  'V1000002775',
  'V1000002771',
  'V1000003484',
  'V1000004012',
  'V1000004118',
  'V1000003772',
  'V1000004269'
]

const GenerateSerialNumbersPage: React.FunctionComponent<GenerateSerialNumbersPageProps> = props => {
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [panelType, setPanelType] = useState<PanelTypeConfig>({})

  const [message, setMessage] = useState<MessageProps>({})
  const [requestIsProcessing, setRequestIsProcessing] = useState(false)
  const { articles, setArticles } = useContext(ArticlesContext)
  useEffect(() => {
    const fetchArticles = async () => {
      if (articles && !isObjectEmpty(articles)) {
        return
      }

      const { items } = await API.get('its-items', '/', {})

      setArticles(items)
    }

    fetchArticles()

    return () => {}
  }, [articles, setArticles])

  useEffect(() => {
    const fetchOrders = async () => {
      setRequestIsProcessing(true)
      try {
        const response = await API.get('its-orders', '/purchase-orders', {
          queryStringParameters: { vendorId: APPROVED_VENDOR_IDS.join(), navStatus: 'Released' },
        })
        setPurchaseOrders(response.items)
        setRequestIsProcessing(false)
        return response
      } catch (e) {
        setRequestIsProcessing(false)
        throw e
      }
    }

    fetchOrders()

    return () => {}
  }, [])

  function showPOInfo(item: PurchaseOrder) {
    setMessage({})
    setPanelType({
      type: 'poInfo',
      data: item,
    })
  }

  function showPOSerialsInfo(item: PurchaseOrder) {
    setMessage({})
    setPanelType({
      type: 'poSerialsInfo',
      data: item,
    })
  }

  function showConfirmSNGForm(data: any, helpers: any) {
    setMessage({})
    setPanelType({
      type: 'confirmForm',
      data: {
        items: data,
        resetForm: helpers.resetForm,
      },
    })
  }

  function showDataMissingForm(errors: any) {
    showDataMissingMessage()
    setPanelType({
      type: 'missingValues',
      data: {
        errors,
      },
    })
  }

  function showSuccessfullyGeneratedMessage() {
    setMessage({
      type: 'success',
      text: 'The serial numbers have now been generated successfully.',
    })
  }

  function showSuccessfullySavedValuesMessage() {
    setMessage({
      type: 'success',
      text: 'The new values have been successfully saved. You can now generate serial numbers for the purchase orders.',
    })
  }

  function showDataMissingMessage() {
    setMessage({
      type: 'error',
      text:
        'The serial numbers cannot be generated since the following article numbers and supply units are missing batchCode, supplyCode and/or colorCode. Please add new values.',
    })
  }

  function showErrorMessage(errorText: string) {
    setMessage({
      type: 'error',
      text: errorText,
    })
  }

  const closePanel = () => {
    setPanelType({})
  }

  const handleSubmitSerialNumbers = async (values: any, resetForm: any) => {
    setRequestIsProcessing(true)
    try {
      const orderIds = values.join(',')
      const response = await API.get('its-batchinfo-generator', '/generate', {
        queryStringParameters: { orderIds },
      }).catch(error => {
        if (error.response && error.response.status === 400) {
          return error.response.data
        }
        throw error
      })

      if (response.success) {
        const excelResponse = await API.get('its-excel-generator', `/supplier-serial`, {
          queryStringParameters: {
            purchaseOrderIds: orderIds,
          },
          responseType: 'text',
          response: true,
        })

        const contentDispositionHeader = excelResponse.headers['content-disposition']

        if (contentDispositionHeader) {
          downloadFile(excelResponse.data, contentDispositionHeader.split('"')[1])
        }
        setRequestIsProcessing(false)
        showSuccessfullyGeneratedMessage()
        closePanel()
        resetForm()
        setPurchaseOrders(
          purchaseOrders.map(order => {
            if (values.includes(order.orderId)) {
              order.serialnumberStatus = 'PROCESSED'
            }
            return order
          }),
        )

        return response
      }

      setRequestIsProcessing(false)
      showDataMissingForm(response.errors)

      return response
    } catch (error) {
      setRequestIsProcessing(false)
      throw error
    }
  }

  const handleSubmitMissingValues = async (rows: MissingValuesPayload[]) => {
    try {
      setRequestIsProcessing(true)
      for (const body of rows) {
        await API.post('its-comp-data', '/serial-number-generation', { body })
      }
      showSuccessfullySavedValuesMessage()
      closePanel()
      setRequestIsProcessing(false)
    } catch (error) {
      setRequestIsProcessing(false)
      const errorMessage = error.response?.data?.message || 'Something went wrong, please contact admin'
      showErrorMessage(errorMessage)
      throw error
    }
  }

  return (
    <div css={pageStyles}>
      <Typography type="h3">Generate serial numbers</Typography>
      <div css={panelsWrapperStyles}>
        <div
          css={css`
            ${purchaseOrdersPanelStyles} ${panelType.type === 'confirmForm' ? disabledPanel : ''}
          `}
        >
          <Typography
            type="snippet"
            css={css`
              margin-bottom: 12px;
            `}
          >
            Search for purchase order
          </Typography>
          <Input
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            type={`search`}
            icoLeft={<Icon name="search" size="s" css={icoLeftStyles} />}
          />
          <Tabs
            type="accordion"
            css={css`
              margin-top: 20px;
            `}
          >
            <Tab name="To be generated" initial>
              <ToBeGeneratedForm
                showPOInfo={showPOInfo}
                searchQuery={searchQuery}
                showConfirmSNGForm={showConfirmSNGForm}
                initialValues={{}}
                purchaseOrders={purchaseOrders}
                requestIsProcessing={requestIsProcessing}
              />
            </Tab>
            <Tab name="Done">
              <DownloadPOForm
                showPOSerialsInfo={showPOSerialsInfo}
                searchQuery={searchQuery}
                purchaseOrders={purchaseOrders}
                requestIsProcessing={requestIsProcessing}
                setRequestIsProcessing={setRequestIsProcessing}
              />
            </Tab>
          </Tabs>
        </div>
        <div>
          {message.type && message.text && (
            <MessageBox
              status={message.type}
              border={true}
              css={css`
                margin-bottom: 12px;
              `}
            >
              {message.text}
            </MessageBox>
          )}
          {panelType && panelType.type === 'confirmForm' && (
            <ConfirmSerialNumberGeneration
              data={panelType.data}
              onClose={closePanel}
              onSubmit={handleSubmitSerialNumbers}
              submitting={requestIsProcessing}
            />
          )}
          {panelType && panelType.type === 'poInfo' && (
            <PONumberInformation purchaseOrder={panelType.data} onClose={closePanel} />
          )}
          {panelType && panelType.type === 'poSerialsInfo' && (
            <PONumberSerialsInformation purchaseOrder={panelType.data} onClose={closePanel} />
          )}
          {panelType && panelType.type === 'missingValues' && panelType.data.errors.length > 0 && (
            <MissingValuesForm
              missingValuesErrors={panelType.data.errors}
              onClose={closePanel}
              onSubmit={handleSubmitMissingValues}
              onSubmitting={requestIsProcessing}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default GenerateSerialNumbersPage
